import { Fragment, useMemo, useContext, useState, useEffect, FC } from 'react';
import Card from '../Card/Card';
import common from '../../util/scss/common.module.scss';
import { IconSuccess } from '../../icons/Icons';
import GlobalContext from '../../contexts/GlobalContext';
import { getClientsStudioUrl, getInitials, replacePhrase } from '../../util/helpers';
import { Button, Link } from '@mui/material';
import styles from './VerifyAccount.module.scss';
import { TUser } from '../../util/types';
import { autoLoginSeconds, EIdentityClientId, EMixpanelEvent } from '../../util/constants';
import CoBranding from '../CoBranding/CoBranding';
import mixpanel from 'mixpanel-browser';

export type TVerifyBusiness = {
    users: {
        id: string;
        firstName: string;
        lastName: string;
    }[];
    name: string;
    city: string;
    stateOrProvince: string;
    countryCode: string;
    subscriberId: string;
};

type TBusinessItemProps = {
    id: string;
    name: string;
    city: string;
    stateOrProvince: string;
    country: string;
    primaryUser: TUser;
    users: TUser[];
    hasFamilyUsers: boolean;
};

type TUserItemProps = {
    isPrimary?: boolean;
    firstName: string;
    lastName: string;
};

type TVerifyAccountProps = {
    title?: string;
    businesses: TVerifyBusiness[];
    primaryUser: TUser;
    hasFamilyUsers: boolean;
    legacySyncToken: string;
    onFindBusinesses?: () => void;
};

const maxBusinesses = 10;

const BusinessItem: FC<TBusinessItemProps> = (props) => {
    const { id, name, city, stateOrProvince, country, primaryUser, users, hasFamilyUsers } = props;
    return (
        <article className={styles.businessItem} data-testid={`business-item`} data-id={`business-${id}`}>
            <header>
                <h3>{name}</h3>
                <p>{`${city}, ${stateOrProvince}`}</p>
            </header>
            {users.length > 0 && hasFamilyUsers && (
                <div className={styles.usersList}>
                    {users.map((user) => (
                        <Fragment key={`${id}-${user.id}`}>
                            <UserItem
                                isPrimary={user.id === primaryUser.id ? true : false}
                                firstName={user.firstName}
                                lastName={user.lastName}
                            />
                        </Fragment>
                    ))}
                </div>
            )}
        </article>
    );
};

const UserItem: FC<TUserItemProps> = (props) => {
    const { isPrimary = false, firstName, lastName } = props;
    const initials = useMemo(() => getInitials(firstName, lastName), [firstName, lastName]);
    return (
        <article
            className={`${styles.userItem} ${isPrimary ? styles.primaryUser : styles.dependentUser}`}
            data-testid={'user-item'}
        >
            <span className={styles.avatar}>{initials}</span>
            <p data-testid={'user-name'}>{`${firstName} ${lastName}`}</p>
        </article>
    );
};

const VerifyAccount: FC<TVerifyAccountProps> = (props) => {
    const {
        language,
        connectBaseUrl,
        fullLoginLink,
        removeFullLoginLinkFromLocalStorage,
        coBranding,
        subscriberId,
        webClientsBaseUrl,
        identityClientId,
    } = useContext(GlobalContext);
    const { title, businesses, primaryUser, hasFamilyUsers, legacySyncToken, onFindBusinesses } = props;
    const [secondsLeft, setSecondsLeft] = useState(autoLoginSeconds);

    const verifyDesc = useMemo(() => {
        if (businesses.length > 0) {
            return `${language.verify.desc.singleUser.hasLinks}`;
        }
        return `${language.verify.desc.singleUser.noLinks}`;
    }, [hasFamilyUsers, businesses]);

    const autoLoginDesc = useMemo(() => {
        const desc1 = replacePhrase(language.verify.autoLogin, '<secondsLeft>', secondsLeft.toString());
        const desc2 = replacePhrase(
            desc1,
            '<secondsLabel>',
            secondsLeft > 1 ? language.common.seconds : language.common.second,
        );
        return desc2;
    }, [secondsLeft]);

    const loginCta = useMemo(() => {
        if (coBranding.name) {
            return replacePhrase(language.verify.cobrandedLoginCta, '<businessName>', coBranding.name);
        } else {
            return language.verify.loginCta;
        }
    }, [coBranding]);

    const webClientsLoginLink = useMemo(
        () => getClientsStudioUrl(webClientsBaseUrl, subscriberId),
        [webClientsBaseUrl, subscriberId],
    );

    const handleLogin = () => {
        mixpanel.track(EMixpanelEvent.interacted, { key: 'verify_account_login' });
        removeFullLoginLinkFromLocalStorage();
        window.location.assign(fullLoginLink);
    };

    const handleWebClientsLogin = () => {
        mixpanel.track(EMixpanelEvent.interacted, { key: 'verify_account_login' });
        removeFullLoginLinkFromLocalStorage();
        window.location.assign(webClientsLoginLink);
    };

    const handleFindBusinesses = () => {
        removeFullLoginLinkFromLocalStorage();
        onFindBusinesses && onFindBusinesses();
        window.location.assign(`${connectBaseUrl}/user/searchAndSync/${legacySyncToken}`);
    };

    useEffect(() => {
        const loginTimer = setTimeout(() => {
            setSecondsLeft(secondsLeft - 1);
        }, 1000);
        return () => clearTimeout(loginTimer);
    }, [secondsLeft]);

    useEffect(() => {
        // if not web clients and we have a login link then log in the user automatically
        secondsLeft === 0 && identityClientId !== EIdentityClientId.webClients && fullLoginLink && handleLogin();
    }, [secondsLeft]);

    return (
        <div className={`${coBranding.brandColor && styles.coBranded}`}>
            {(coBranding.logoUrl || coBranding.name) && (
                <CoBranding logoUrl={coBranding.logoUrl} name={coBranding.name} />
            )}
            <section className={`${common.icon}`}>
                <IconSuccess width={'50'} height={'50'} fillColor={'777982'} />
            </section>
            <header>
                <h1 className={`${common.pageHeader} ${common.withSubhead}`}>
                    {title ? title : language.verify.header}
                </h1>
                <p className={`${common.pageSubhead}`} data-testid={'verify-desc'}>
                    {verifyDesc}
                </p>
            </header>
            {businesses.length > 0 && (
                <section className={styles.businessesList}>
                    {businesses.map((business, index) => {
                        if (index < maxBusinesses) {
                            return (
                                <Fragment key={business.subscriberId}>
                                    <BusinessItem
                                        id={business.subscriberId}
                                        name={business.name}
                                        city={business.city}
                                        stateOrProvince={business.stateOrProvince}
                                        country={business.countryCode}
                                        users={business.users}
                                        primaryUser={primaryUser}
                                        hasFamilyUsers={hasFamilyUsers}
                                    />
                                </Fragment>
                            );
                        }
                        return null;
                    })}
                </section>
            )}
            {businesses.length > maxBusinesses && (
                <section className={styles.moreBusinesses}>
                    <Link onClick={handleFindBusinesses} role="button">
                        {language.verify.moreBusinessesCta}
                    </Link>
                </section>
            )}
            {identityClientId === EIdentityClientId.webClients ? (
                <footer className={styles.login}>
                    <Button
                        variant="contained"
                        type="button"
                        onClick={handleWebClientsLogin}
                        size="large"
                        data-testid="web-clients-login"
                    >
                        {loginCta}
                    </Button>
                </footer>
            ) : fullLoginLink ? (
                <footer className={styles.login}>
                    <Button
                        variant="contained"
                        type="button"
                        onClick={handleLogin}
                        size="large"
                        data-testid="connect-authorize-login"
                    >
                        {loginCta}
                    </Button>
                    <p>{autoLoginDesc}</p>
                </footer>
            ) : (
                legacySyncToken && (
                    <footer className={styles.otherBusiness}>
                        <p>{language.verify.otherBusiness}</p>
                        <Button
                            variant="contained"
                            type="button"
                            onClick={handleFindBusinesses}
                            size="large"
                            data-testid="connect-sync"
                        >
                            {language.verify.findBusinessesCta}
                        </Button>
                    </footer>
                )
            )}
        </div>
    );
};

export default VerifyAccount;
